/**
 * A Stimulus controller that add a file drop zone to a form.
 *
 * example:
 * = render "shared/file_drop_zone", form:, multiple: true, field_name: "files",
 *   accept: "image/png,image/jpeg,application/pdf", max_size: 1.megabytes
 */

import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['input', 'dropZone', 'preview'];

  DEFAULT_MAX_FILE_SIZE = 10 * 1024 * 1024;

  DEFAULT_ALLOWED_FORMATS = ['image/jpeg', 'image/png', 'application/pdf'];

  connect() {
    const formats = this.dropZoneTarget.dataset.fileDropZoneFormats;
    const maxSize = this.dropZoneTarget.dataset.fileDropZoneMaxSize;

    this.maxFileSize = maxSize ? parseInt(maxSize, 10) : this.DEFAULT_MAX_FILE_SIZE;
    this.allowedFormats = formats ? formats.split(',') : this.DEFAULT_ALLOWED_FORMATS;

    this.inputTarget.addEventListener('change', this.uploadFiles.bind(this));
    this.inputTarget.setAttribute('accept', this.allowedFormats.join(','));
  }

  openFileDialog() {
    this.inputTarget.value = '';
    this.inputTarget.click();
  }

  dragOver(event) {
    event.preventDefault();
    this.dropZoneTarget.classList.add('dragging');
  }

  dragLeave(event) {
    event.preventDefault();
    this.dropZoneTarget.classList.remove('dragging');
  }

  dropFiles(event) {
    event.preventDefault();
    this.dropZoneTarget.classList.remove('dragging');

    const files = event.dataTransfer.files;
    const validFiles = this.validateAndPreview(files);
    this.addFilesToInput(validFiles);
  }

  uploadFiles() {
    const files = this.inputTarget.files;
    this.validateAndPreview(files);
  }

  addFilesToInput(files) {
    const dataTransfer = new DataTransfer();

    Array.from(this.inputTarget.files).forEach((file) => dataTransfer.items.add(file));
    Array.from(files).forEach((file) => dataTransfer.items.add(file));

    this.inputTarget.files = dataTransfer.files;
  }

  validateAndPreview(files) {
    this.previewTarget.innerHTML = '';
    const validFiles = [];
    const errors = [];

    Array.from(files).forEach((file) => {
      if (!this.allowedFormats.includes(file.type)) {
        errors.push(`❌ Invalid format: ${file.name}`);
      } else if (file.size > this.maxFileSize) {
        errors.push(`❌ File too large: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      const errorList = document.createElement('ul');
      errors.forEach((error) => {
        const errorItem = document.createElement('li');
        errorItem.textContent = error;
        errorList.appendChild(errorItem);
      });
      this.previewTarget.appendChild(errorList);
    }

    validFiles.forEach((file) => {
      const fileElement = document.createElement('p');
      fileElement.textContent = `✅ ${file.name} (${(file.size / 1024).toFixed(2)} KB)`;
      this.previewTarget.appendChild(fileElement);
    });

    return validFiles;
  }
}
